import { useContext, useEffect, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import {
    loadModuleScript,
    payloadForState,
    statusMessage,
    STATUS_LABELS,
    widgetAttributesForMode,
} from './altcha-widget-options';

// Le widget est SERVI depuis `public/vendor/altcha/` plutôt qu'importé du paquet
// npm : le serveur de développement tourne dans un conteneur dont les
// `node_modules` viennent de son image, où une dépendance ajoutée au dépôt
// n'existe pas — le module qui l'importerait répondrait 500. Servi par
// `public/`, il ne passe par aucune résolution de paquet, en dev comme dans le
// build (où `public/` est recopié tel quel). Provenance et mise à jour :
// `front_app/public/vendor/altcha/README.md`.
const WIDGET_URL = '/vendor/altcha/altcha.js';
const I18N_URL = '/vendor/altcha/fr-fr.js';

/**
 * AltchaField — preuve « je ne suis pas un robot » posée dans le store.
 *
 * Pourquoi du JS et pas du YAML : aucun rendu déclaratif ne peut piloter une
 * preuve de travail — récupérer un challenge signé, itérer un compteur dans des
 * Web Workers jusqu'à retrouver la clé dérivée, écouter le cycle de vie d'un
 * custom element (`verified`, `expired`, `error`) — ni faire redescendre le
 * jeton obtenu dans les données. C'est exactement ce chaînon que ce champ
 * ajoute : il ne connaît ni le formulaire, ni l'endpoint, ni le métier ; il
 * écrit une chaîne à un `dataLocation`, que le YAML envoie ensuite comme
 * n'importe quelle autre valeur.
 *
 * Le module Drupal `altcha_headless` sert le challenge et vérifie le jeton ;
 * côté visiteur, aucun cookie, aucun appel tiers, aucune énigme.
 *
 * Props YAML :
 *   dataLocation   – liaison de formulaire standard (reçoit le jeton ou '')
 *   mode           – `invisible` (défaut) ou `checkbox`
 *   challengeUrl   – défaut `/api/altcha/challenge`
 *   language       – code i18n du widget (défaut `fr-fr`)
 *   configuration  – objet d'options avancées du widget (JSON)
 *   widgetUrl      – script du widget (défaut `/vendor/altcha/altcha.js`)
 *   i18nUrl        – traduction du widget (défaut `/vendor/altcha/fr-fr.js`)
 *   statusLabels   – libellés annoncés par état (défaut : STATUS_LABELS)
 *   attributes     – sur l'enveloppe
 *
 * Exemple :
 *   - type: AltchaField
 *     dataLocation: ~~.contactForm.captcha
 *     mode: invisible
 */
export const AltchaField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const resolve = (valueToEvaluate) =>
        evaluateTemplateValue({ valueToEvaluate, globalDataContext, templateContext });

    const { formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: '',
        globalDataContext,
        templateContext,
    });

    const attributes = useEvaluatedAttributes(props.attributes ?? {});
    const mode = resolve(props.mode);
    const { auto, type } = widgetAttributesForMode(mode);
    const challengeUrl = resolve(props.challengeUrl) || '/api/altcha/challenge';
    const language = resolve(props.language) || 'fr-fr';
    const widgetUrl = resolve(props.widgetUrl) || WIDGET_URL;
    const i18nUrl = resolve(props.i18nUrl) || I18N_URL;
    const configuration = resolve(props.configuration);
    // Gardés dans une référence : les libellés se relisent à chaque rendu, mais
    // ne doivent pas faire réattacher l'écouteur d'événement à chaque fois.
    const statusLabels = useRef(STATUS_LABELS);
    statusLabels.current = { ...STATUS_LABELS, ...(resolve(props.statusLabels) ?? {}) };

    const widgetRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [status, setStatus] = useState('');

    // Le widget ne sert qu'aux formulaires publics : on ne le charge qu'ici, à
    // la demande, et par balise script (cf. `loadModuleScript`).
    useEffect(() => {
        let alive = true;
        loadModuleScript(widgetUrl)
            // La traduction est un confort : si elle manque, le widget parle
            // anglais, il ne doit pas cesser de protéger le formulaire.
            .then(() => loadModuleScript(i18nUrl).catch(() => undefined))
            .then(() => alive && setLoaded(true))
            .catch((error) => console.error('ALTCHA : widget non chargé', error));

        return () => {
            alive = false;
        };
    }, [widgetUrl, i18nUrl]);

    useEffect(() => {
        const element = widgetRef.current;
        if (!loaded || !element) {
            return undefined;
        }

        // `statechange` couvre tout le cycle : il porte la preuve quand elle
        // existe et vide le champ dès qu'elle cesse d'être valable.
        const onStateChange = (event) => {
            const state = event?.detail?.state;
            globalDataContext.updateData(payloadForState(state, event, element), formDataPath);
            setStatus(statusMessage(state, statusLabels.current));
        };

        element.addEventListener('statechange', onStateChange);

        return () => element.removeEventListener('statechange', onStateChange);
    }, [loaded, formDataPath, globalDataContext]);

    // Rien à afficher tant que le custom element n'est pas défini : le
    // navigateur rendrait une balise inerte, sans état ni libellé.
    const widget = loaded ? (
        <altcha-widget
            ref={widgetRef}
            challenge={challengeUrl}
            auto={auto}
            type={type}
            language={language}
            {...(configuration ? { configuration: JSON.stringify(configuration) } : {})}
        />
    ) : null;

    // `altcha-on-surface` (recette du DS, cf. index.css) fait prendre au widget
    // les couleurs du bloc qui l'accueille — la carte blanche par défaut ferait
    // une tache sur une section sombre. Une classe passée en `attributes` s'y
    // ajoute, elle ne la remplace pas.
    const { class: yamlClass, ...wrapperAttributes } = attributes;
    const className = ['altcha-on-surface', yamlClass].filter(Boolean).join(' ');

    return (
        <ActionDependant {...props}>
            <div {...wrapperAttributes} className={className}>
                {widget}
                {/* Le widget n'annonce rien : cette région dit à voix haute ce
                    que la carte montre à l'écran, sans déplacer le focus. */}
                <p className="sr-only" role="status">{status}</p>
            </div>
        </ActionDependant>
    );
};
